import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { useForm, usePage, router } from '@inertiajs/react';
import { AffiliateLayout } from '../../../design-system/components/layouts';
import { Card, CardBody, Button, Input } from '../../../design-system/components';
import {
  affiliate_onboarding_update_coupon_path,
  affiliate_onboarding_skip_coupon_path,
  affiliate_check_coupon_availability_path,
} from '../../../lib/routes';
export default function Coupon({ current_coupon_code, discount_display, slug, step = 2, total_steps = 2 }) {
  const { props } = usePage();
  const flash = props.flash;
  const form = useForm({
    coupon_code: current_coupon_code || '',
  });
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [skipping, setSkipping] = useState(false);
  const debounceRef = useRef(undefined);
  const checkAvailability = useCallback((code) => {
    if (code.length < 3) {
      setStatus('invalid');
      setStatusMessage('Must be at least 3 characters');
      return;
    }
    if (!/^[A-Z0-9]+$/.test(code)) {
      setStatus('invalid');
      setStatusMessage('Only uppercase letters and numbers allowed');
      return;
    }
    setStatus('checking');
    setStatusMessage('Checking...');
    fetch(`${affiliate_check_coupon_availability_path()}?coupon_code=${encodeURIComponent(code)}`, {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setStatus(data.available ? 'available' : 'unavailable');
        setStatusMessage(data.message);
      })
      .catch(() => {
        setStatus('idle');
        setStatusMessage('');
      });
  }, []);
  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    form.setData('coupon_code', value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setStatus('idle');
      setStatusMessage('');
      return;
    }
    debounceRef.current = setTimeout(() => checkAvailability(value), 300);
  };
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  const handleSubmit = (e) => {
    e.preventDefault();
    form.post(affiliate_onboarding_update_coupon_path());
  };
  const handleSkip = () => {
    if (confirm('Are you sure? You can set up a coupon code later from your dashboard.')) {
      setSkipping(true);
      router.post(affiliate_onboarding_skip_coupon_path());
    }
  };
  const statusIndicator = () => {
    switch (status) {
      case 'checking':
        return <span className="text-xs font-medium text-fg-tertiary">Checking...</span>;
      case 'available':
        return (
          <span className="text-xs font-medium text-green-600 flex items-center gap-1">
            <Check size={14} />
            Available
          </span>
        );
      case 'unavailable':
        return (
          <span className="text-xs font-medium text-red-600 flex items-center gap-1">
            <X size={14} />
            Unavailable
          </span>
        );
      default:
        return null;
    }
  };
  const canSubmit = form.data.coupon_code.length >= 3 && status === 'available' && !form.processing;
  return (
    <AffiliateLayout>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-medium text-fg-tertiary mb-2">Step {step} of {total_steps}</p>
          <h1 className="text-2xl font-semibold text-fg tracking-tight">
            Create your coupon code
          </h1>
          <p className="mt-2 text-sm text-fg-tertiary">
            Your audience can use this code for{' '}
            <span className="font-semibold text-fg">{discount_display}</span> their
            subscription.
          </p>
        </div>

        {flash?.alert && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            {flash.alert}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <Input
                  label="Coupon code"
                  name="coupon_code"
                  id="coupon_code"
                  value={form.data.coupon_code}
                  onChange={handleCodeChange}
                  placeholder={`e.g. ${(slug || 'SAVE').toUpperCase().replace(/-/g, '')}${discount_display?.match(/\d+/)?.[0] || '10'}`}
                  autoComplete="off"
                  minLength={3}
                  maxLength={20}
                  className="uppercase"
                  error={statusMessage && (status === 'invalid' || status === 'unavailable') ? statusMessage : undefined}
                  helperText="Uppercase letters and numbers only. 3–20 characters."
                  suffix={statusIndicator()}
                />

                {form.errors.coupon_code && (
                  <p className="text-sm text-red-600">{form.errors.coupon_code}</p>
                )}
              </div>
            </CardBody>
          </Card>

          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={skipping || form.processing}
            >
              Skip for now
            </Button>
            <Button type="submit" disabled={!canSubmit} loading={form.processing} size="lg">
              Create Coupon
            </Button>
          </div>
        </form>
      </div>
    </AffiliateLayout>
  );
}
